import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import './NewDashboard.css';
import './AdminDashboard.css';
import ActiveRegionalBadge from './common/ActiveRegionalBadge';
import LoadingSpinner from './common/LoadingSpinner';
import { useAuth } from '../contexts/AuthContext';
import RegionalService from '../services/RegionalService';
import areaService from '../services/AreaService';
import materiaService from '../services/MateriaService';
import adminAsistenteService from '../services/AdminAsistenteService';
import AsistenteService from '../services/AsistenteService';
import { filterAdminMenuItems, isAdminArea } from '../utils/adminMenu';
import tipoAsistenteService from '../services/TipoAsistenteService';
import './NewAssistant.css';

const menuItems = [
  { id: 'resumen', label: 'Dashboard', icon: 'fas fa-chart-line' },
  { id: 'profesores', label: 'Profesores', icon: 'fas fa-chalkboard-teacher' },
  // { id: 'alumnos', label: 'Alumnos', icon: 'fas fa-user-graduate' },
  { id: 'areas', label: 'Areas', icon: 'fas fa-sitemap' },
  { id: 'admin-area', label: 'Admins de área', icon: 'fas fa-user-tie' },
  { id: 'materias', label: 'Materias', icon: 'fas fa-book' },
  { id: 'asistentes', label: 'Asistentes OpenAI', icon: 'fas fa-robot' },
  { id: 'asistentes-gpt', label: 'Asistentes GPT', icon: 'fas fa-robot' },
];

const INSTITUCIONAL_ID = '61A351C4-64C5-42A3-88D1-E27F092551CD';
const MAX_FILES = 20;
const MAX_FILE_NAME_LENGTH = 'LED2025-planificacion_logica_y_estructu'.length;

const formatFileName = (name) => {
  if (!name) return '';
  return name.length > MAX_FILE_NAME_LENGTH ? `${name.slice(0, MAX_FILE_NAME_LENGTH)}...` : name;
};

const AdminEditAsistente = () => {
  const navigate = useNavigate();
  const { assistantId } = useParams();
  const { user, logout } = useAuth();
  const activeRegional = useMemo(() => RegionalService.getActiveRegional(), []);
  const activeRegionalId = useMemo(() => RegionalService.getActiveRegionalId(), []);
  const activeRegionalName = activeRegional?.nombre || activeRegional?.name || activeRegional?.descripcion || 'Regional seleccionado';
  const isGptClassicEnabled = useMemo(() => {
    const flags = RegionalService.getFeatureFlags();
    return Array.isArray(flags) && flags.some((flag) => {
      const nombre = (flag?.nombre || flag?.Nombre || '').toLowerCase();
      return nombre === 'asistentesclasicos gpt' && Boolean(flag?.activo ?? flag?.Activo);
    });
  }, []);
  const filteredMenuItems = useMemo(() => filterAdminMenuItems(menuItems, user).filter((item) => isGptClassicEnabled || isAdminArea(user) || item.id !== 'asistentes-gpt'), [user, isGptClassicEnabled]);
  const canSelectTipo = true;

  const [form, setForm] = useState({
    idOpenAIAssistant: '',
    nombre: '',
    nicknameUrl: '',
    idTipoAsistente: INSTITUCIONAL_ID,
    descripcion: '',
    promptExtra: '',
    idRegional: activeRegionalId || '',
    idArea: '',
    idMateria: '',
    publico: true,
    passwordAsistente: ''
  });
  const [areas, setAreas] = useState([]);
  const [materias, setMaterias] = useState([]);
  const [tiposAsistente, setTiposAsistente] = useState([]);
  const [isFetching, setIsFetching] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [existingFiles, setExistingFiles] = useState([]);
  const [newFiles, setNewFiles] = useState([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  useEffect(() => {
    if (!assistantId) {
      navigate('/admin-dashboard', { replace: true, state: { tab: 'asistentes' } });
    }
  }, [assistantId, navigate]);

  useEffect(() => {
    const fetchAreasMaterias = async () => {
      try {
        const tasks = [
          areaService.getAreasActivas(activeRegionalId),
          materiaService.getMateriasActivas()
        ];
        if (canSelectTipo) {
          tasks.push(tipoAsistenteService.getTiposActivos());
        }
        const [areasList, materiasList, tiposList] = await Promise.all(tasks);
        const filteredMaterias = (materiasList || []).filter((m) => (m.idRegional || m.IdRegional) === activeRegionalId);
        setAreas(Array.isArray(areasList) ? areasList : []);
        setMaterias(Array.isArray(filteredMaterias) ? filteredMaterias : []);
        if (canSelectTipo) {
          setTiposAsistente(Array.isArray(tiposList) ? tiposList : []);
        }
      } catch (error) {
        setMessage(error.message || 'No pudimos cargar áreas o materias.');
        setMessageType('error');
      }
    };
    if (activeRegionalId) {
      fetchAreasMaterias();
    }
  }, [activeRegionalId, canSelectTipo]);

  useEffect(() => {
    const fetchAsistente = async () => {
      if (!assistantId) return;
      setIsFetching(true);
      try {
        const data = await adminAsistenteService.getAsistenteById(assistantId);
        setForm({
          idOpenAIAssistant: data.idOpenAIAssistant || assistantId,
          nombre: data.nombre || '',
          nicknameUrl: data.nicknameUrl || data.NicknameUrl || '',
          idTipoAsistente: canSelectTipo ? (data.idTipoAsistente || data.IdTipoAsistente || INSTITUCIONAL_ID) : INSTITUCIONAL_ID,
          descripcion: data.descripcion || '',
          promptExtra: data.promptExtra || data.PromptExtra || '',
          idRegional: data.idRegional || activeRegionalId || '',
          idArea: data.idArea || '',
          idMateria: data.idMateria || '',
          publico: data.publico ?? data.Publico ?? true,
          passwordAsistente: data.passwordAsistente || data.PasswordAsistente || ''
        });
        const archivos = data.archivos || data.Archivos || [];
        if (Array.isArray(archivos)) {
          setExistingFiles(
            archivos.map((archivo) => ({
              fileId: archivo.id || archivo.Id,
              fileName: archivo.nombreArchivo || archivo.NombreArchivo || archivo.fileName || archivo.FileName,
              fechaAlta: archivo.fechaAlta || archivo.FechaAlta
            }))
          );
        } else {
          setExistingFiles([]);
        }
        setNewFiles([]);
      } catch (error) {
        setMessage(error.message || 'No pudimos cargar el asistente.');
        setMessageType('error');
      } finally {
        setIsFetching(false);
      }
    };

    fetchAsistente();
  }, [assistantId, activeRegionalId]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddFiles = (files) => {
    if (!files || files.length === 0) return;

    const pdfFiles = files.filter((file) => file.type === 'application/pdf');
    if (pdfFiles.length !== files.length) {
      setMessage('Solo se permiten archivos PDF.');
      setMessageType('error');
    }

    setNewFiles((prev) => {
      const availableSlots = MAX_FILES - (existingFiles.length + prev.length);
      if (availableSlots <= 0) {
        setMessage('Se alcanzó el límite máximo de archivos.');
        setMessageType('error');
        return prev;
      }

      const filesToAdd = pdfFiles.slice(0, availableSlots);
      if (filesToAdd.length < pdfFiles.length) {
        setMessage(`Solo se pueden agregar ${availableSlots} archivos adicionales.`);
        setMessageType('error');
      }

      return [...prev, ...filesToAdd];
    });
  };

  const handleRemoveNewFile = (index) => {
    setNewFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSaveClick = () => {
    if (!form.nombre.trim()) {
      setMessage('Completa el nombre del asistente.');
      setMessageType('error');
      return;
    }
    if (!form.idArea) {
      setMessage('Selecciona un área.');
      setMessageType('error');
      return;
    }
    if (!form.publico && !form.passwordAsistente.trim()) {
      setMessage('Completa el password del asistente.');
      setMessageType('error');
      return;
    }

    if (newFiles.length > 0 && !showConfirmation) {
      setShowConfirmation(true);
      return;
    }

    submitUpdate();
  };

  const submitUpdate = async () => {
    setIsSaving(true);
    setMessage('Guardando cambios...');
    setMessageType('info');
    try {
      const tipoId = canSelectTipo ? (form.idTipoAsistente || INSTITUCIONAL_ID) : INSTITUCIONAL_ID;
      const payload = {
        nombre: form.nombre,
        idTipoAsistente: tipoId,
        descripcion: form.descripcion,
        promptExtra: form.promptExtra,
        nicknameUrl: form.nicknameUrl,
        idRegional: activeRegionalId,
        idArea: form.idArea,
        idMateria: form.idMateria || null,
        publico: form.publico
      };

      payload.conPassword = !form.publico;
      if (!form.publico && form.passwordAsistente.trim()) {
        payload.passwordAsistente = form.passwordAsistente.trim();
      }

      await adminAsistenteService.updateAsistente(assistantId, payload);

      const formDataToSend = new FormData();
      formDataToSend.append('nombre', form.nombre.trim());
      formDataToSend.append('descripcion', form.descripcion || '');
      formDataToSend.append('nicknameUrl', form.nicknameUrl || '');
      formDataToSend.append('promptExtra', form.promptExtra || '');
      if (activeRegionalId) {
        formDataToSend.append('idRegional', activeRegionalId);
      }
      if (form.idArea) {
        formDataToSend.append('idArea', form.idArea);
      }
      if (form.idMateria) {
        formDataToSend.append('idMateria', form.idMateria);
      }
      formDataToSend.append('publico', String(form.publico));
      formDataToSend.append('conPassword', String(!form.publico));
      if (!form.publico && form.passwordAsistente.trim()) {
        formDataToSend.append('passwordAsistente', form.passwordAsistente.trim());
      }
      newFiles.forEach((file) => {
        formDataToSend.append('archivosPdf', file);
      });
      await AsistenteService.actualizarAsistente(assistantId, formDataToSend);

      setMessage('Asistente actualizado correctamente.');
      setMessageType('success');
      setTimeout(() => navigate('/admin-dashboard', { replace: true, state: { tab: 'asistentes' } }), 900);
    } catch (error) {
      setMessage(error.message || 'No pudimos actualizar el asistente.');
      setMessageType('error');
    } finally {
      setIsSaving(false);
      setShowConfirmation(false);
    }
  };

  return (
    <div className="new-dashboard admin-mode">
      <aside className="dashboard-sidebar">
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <i className="fas fa-university"></i>
            <span>Admin</span>
          </div>
        </div>
        <nav className="sidebar-nav">
          {filteredMenuItems.map((item) => (
            <button
              key={item.id}
              className={`nav-item ${item.id === 'asistentes' ? 'active' : ''}`}
              onClick={() => navigate('/admin-dashboard', { state: { tab: item.id } })}
            >
              <i className={item.icon}></i>
              <span>{item.label}</span>
            </button>
          ))}
        </nav>
        <div className="sidebar-footer">
          <ActiveRegionalBadge />
          <div className="sidebar-user">
            <div className="user-avatar">
              <i className="fas fa-user-shield"></i>
            </div>
            <div className="user-info">
              <span className="user-name">{user?.displayName || user?.name || user?.email || 'Administrador'}</span>
              <span className="user-role">Admin</span>
            </div>
          </div>
        </div>
      </aside>

      <main className="dashboard-main">
        <header className="dashboard-header">
          <div className="header-left">
            <h1 className="page-title">Editar asistente</h1>
            <p className="page-subtitle">Ajusta la información del asistente en el regional {activeRegionalName}.</p>
          </div>
          <div className="header-right">
            <ActiveRegionalBadge />
            <div className="user-menu">
              <button className="user-menu-trigger" onClick={logout}>
                <div className="user-avatar">
                  <i className="fas fa-user-shield"></i>
                </div>
                <i className="fas fa-sign-out-alt"></i>
              </button>
            </div>
          </div>
        </header>

        <div className="dashboard-content" style={{ padding: '2rem' }}>
          {isFetching && (
            <div className="loading-container" style={{ padding: '1.5rem 0' }}>
              <LoadingSpinner message="Cargando asistente..." />
            </div>
          )}
          {!isFetching && (
          <div className="content-card">
            <div className="card-header">
              <div className="card-title">
                <i className="fas fa-robot" />
                <span>Datos del asistente</span>
              </div>
            </div>
            <div className="card-content">
              <p style={{ marginTop: 0, color: '#475569' }}>
                El área es obligatoria; la materia puede quedar vacía.
              </p>
              <form className="prof-create-form">
                <div className="form-grid">
                  <div className="form-field">
                    <label>ID OpenAI Assistant</label>
                    <input type="text" value={form.idOpenAIAssistant} disabled />
                  </div>
                  <div className="form-field">
                    <label>Nombre</label>
                    <input
                      type="text"
                      value={form.nombre}
                      onChange={(e) => handleChange('nombre', e.target.value)}
                      placeholder="Asistente X"
                    />
                  </div>
                  <div className="form-field">
                    <label>Nickname URL</label>
                    <input
                      type="text"
                      value={form.nicknameUrl}
                      onChange={(e) => handleChange('nicknameUrl', e.target.value)}
                      placeholder="ej: algebra-lineal-1"
                    />
                  </div>
                  <div className="form-field">
                    <label>Tipo de asistente</label>
                    {canSelectTipo ? (
                      <select
                        value={form.idTipoAsistente || INSTITUCIONAL_ID}
                        onChange={(e) => handleChange('idTipoAsistente', e.target.value)}
                      >
                        <option value="">Selecciona un tipo</option>
                        {tiposAsistente.map((tipo) => (
                          <option key={tipo.id || tipo.Id} value={tipo.id || tipo.Id}>
                            {tipo.nombre || tipo.Nombre}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input type="text" value="Institucional" disabled />
                    )}
                  </div>
                  <div className="form-field">
                    <label>Área (obligatorio)</label>
                    <select value={form.idArea} onChange={(e) => handleChange('idArea', e.target.value)}>
                      <option value="">Selecciona un área</option>
                      {areas.map((a) => (
                        <option key={a.id || a.Id} value={a.id || a.Id}>
                          {a.nombre || a.Nombre}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-field">
                    <label>Materia (opcional)</label>
                    <select value={form.idMateria} onChange={(e) => handleChange('idMateria', e.target.value)}>
                      <option value="">Sin materia</option>
                      {materias.map((m) => (
                        <option key={m.id || m.Id} value={m.id || m.Id}>
                          {m.nombre || m.Nombre}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-field">
                  <label>Descripción</label>
                  <textarea
                    value={form.descripcion}
                    onChange={(e) => handleChange('descripcion', e.target.value)}
                    placeholder="Descripción opcional"
                    style={{ borderRadius: '10px', border: '1px solid var(--gray-200)', padding: '0.65rem 0.85rem', fontSize: '0.95rem', minHeight: '80px' }}
                  />
                </div>

                <div className="form-field">
                  <label>Prompt extra (opcional)</label>
                  <textarea
                    value={form.promptExtra}
                    onChange={(e) => handleChange('promptExtra', e.target.value)}
                    placeholder="Aclaraciones adicionales para el asistente"
                    style={{ borderRadius: '10px', border: '1px solid var(--gray-200)', padding: '0.65rem 0.85rem', fontSize: '0.95rem', minHeight: '110px' }}
                  />
                </div>

                <div className="form-grid">
                  <div className="form-field">
                    <label>Visibilidad</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                      <input
                        type="checkbox"
                        checked={form.publico}
                        onChange={(e) => {
                          const nextValue = e.target.checked;
                          setForm((prev) => ({
                            ...prev,
                            publico: nextValue,
                            passwordAsistente: nextValue ? '' : prev.passwordAsistente
                          }));
                        }}
                      />
                      <span>Asistente público</span>
                    </div>
                  </div>
                </div>

                {!form.publico && (
                  <div className="form-field">
                    <label>Password</label>
                    <input
                      type="text"
                      value={form.passwordAsistente}
                      onChange={(e) => handleChange('passwordAsistente', e.target.value)}
                      placeholder="Ingresa el password (obligatorio)"
                    />
                  </div>
                )}

                {message && (
                  <div
                    className="modal-message"
                    style={{
                      marginTop: '0.5rem',
                      borderColor: messageType === 'success' ? '#bbf7d0' : messageType === 'error' ? '#fecaca' : 'var(--gray-200)',
                      background: messageType === 'success' ? '#dcfce7' : messageType === 'error' ? '#fee2e2' : '#f8fafc',
                      color: messageType === 'error' ? '#7f1d1d' : messageType === 'success' ? '#0f5132' : 'var(--gray-800)'
                    }}
                  >
                    {isSaving && <i className="fas fa-spinner fa-spin" style={{ marginRight: '0.35rem' }} />}
                    {message}
                  </div>
                )}

                <div className="form-section files-section" style={{ marginTop: '1.5rem' }}>
                  <div className="section-header">
                    <div className="section-icon">
                      <i className="fas fa-file-pdf"></i>
                    </div>
                    <div className="section-text">
                      <h3 className="section-title">Archivos del asistente</h3>
                      <p className="section-description">Agrega nuevos PDFs para reemplazar los actuales</p>
                    </div>
                  </div>

                  <div className="files-management">
                    <div className="existing-files-panel">
                      <div className="existing-files-header">
                        <h4>Archivos actuales ({existingFiles.length})</h4>
                      </div>
                      {existingFiles.length === 0 ? (
                        <p className="no-files-text">No hay archivos cargados todavía.</p>
                      ) : (
                        <ul className="existing-files-list">
                          {existingFiles.map((file) => (
                            <li key={file.fileId || file.fileName}>
                              <i className="fas fa-file-pdf"></i>
                              <span>{formatFileName(file.fileName)}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>

                    <div
                      className={`file-dropzone ${isDragOver ? 'dragover' : ''} ${newFiles.length > 0 ? 'has-files' : ''}`}
                      onDragOver={(e) => {
                        e.preventDefault();
                        setIsDragOver(true);
                      }}
                      onDragLeave={(e) => {
                        e.preventDefault();
                        setIsDragOver(false);
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        setIsDragOver(false);
                        const files = Array.from(e.dataTransfer.files);
                        handleAddFiles(files);
                      }}
                    >
                      <div className="dropzone-border">
                        <div className="dropzone-content">
                          <div className="dropzone-icon">
                            <i className="fas fa-cloud-upload-alt"></i>
                          </div>
                          <div className="dropzone-text">
                            <h4>Arrastra archivos PDF o selecciona desde tu equipo</h4>
                            <p>Los archivos nuevos reemplazarán los existentes</p>
                          </div>
                          <div className="dropzone-divider">
                            <span>o</span>
                          </div>
                          <label className="file-input-label">
                            <input
                              type="file"
                              multiple
                              accept=".pdf"
                              onChange={(e) => {
                                const files = Array.from(e.target.files);
                                handleAddFiles(files);
                                e.target.value = '';
                              }}
                              style={{ display: 'none' }}
                            />
                            <span className="btn btn-upload">
                              <i className="fas fa-folder-open"></i>
                              Seleccionar Archivos PDF
                            </span>
                          </label>
                          <div className="upload-limits">
                            <div className="limit-item">
                              <i className="fas fa-file-alt"></i>
                              <span>Máximo 20 archivos</span>
                            </div>
                            <div className="limit-item">
                              <i className="fas fa-file-pdf"></i>
                              <span>Solo formato PDF</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {(existingFiles.length > 0 || newFiles.length > 0) && (
                    <div className="selected-files">
                      <h4>Total de archivos ({existingFiles.length + newFiles.length}/{MAX_FILES})</h4>
                      {newFiles.length > 0 && (
                        <>
                          <p style={{ marginTop: '0.35rem', color: '#b91c1c' }}>
                            Al guardar, se reemplazarán todos los archivos actuales por los nuevos.
                          </p>
                          <h5>Nuevos archivos seleccionados</h5>
                          <div className="files-list">
                            {newFiles.map((file, index) => (
                              <div key={`${file.name}-${index}`} className="file-item">
                                <div className="file-info">
                                  <i className="fas fa-file-pdf"></i>
                                  <span className="file-name">{formatFileName(file.name)}</span>
                                  <span className="file-size">
                                    {(file.size / 1024 / 1024).toFixed(2)} MB
                                  </span>
                                </div>
                                <button
                                  type="button"
                                  className="remove-file-btn"
                                  title="Eliminar archivo"
                                  onClick={() => handleRemoveNewFile(index)}
                                >
                                  <i className="fas fa-times"></i>
                                </button>
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>

                <div className="form-actions">
                  <button type="button" className="btn btn-secondary" onClick={() => navigate('/admin-dashboard', { state: { tab: 'asistentes' } })}>
                    Cancelar
                  </button>
                  <button type="button" className="btn btn-primary" disabled={isSaving} onClick={handleSaveClick}>
                    {isSaving ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-save" />}
                    <span>Guardar cambios</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
          )}
        </div>
      </main>
      {showConfirmation && (
        <div className="confirm-modal-overlay">
          <div className="confirm-modal">
            <h3>Reemplazar archivos del asistente</h3>
            <p>Se actualizará el asistente, reemplazando todos sus archivos actuales por los nuevos que acabas de adjuntar.</p>
            <div className="confirm-modal-actions">
              <button className="btn btn-outline" type="button" onClick={() => setShowConfirmation(false)}>
                Cancelar
              </button>
              <button className="btn btn-primary" type="button" onClick={() => { setShowConfirmation(false); submitUpdate(); }}>
                Entiendo, actualizar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminEditAsistente;
